import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Trophy, RotateCcw } from "lucide-react";
import { PageHeader, Loader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CupsBoard } from "@/components/cups/CupsBoard";
import { CupsHistory } from "@/components/cups/CupsHistory";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtPKR } from "@/lib/format";

const CLOSED = { disabled: "The game is currently disabled by admin.", out_of_hours: "The game is outside its operating hours.", out_of_schedule: "The game is outside its scheduled dates." };

const ResultBox = ({ round, onAgain }) => (
  <div className={`mt-4 rounded-2xl p-5 text-center border ${round.status === "won" ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"}`} data-testid="cups-result">
    {round.status === "won" ? <Trophy className="w-8 h-8 text-yellow-400 mx-auto" /> : <RotateCcw className="w-8 h-8 text-red-400 mx-auto" />}
    <p className={`font-head text-3xl font-extrabold mt-2 ${round.status === "won" ? "text-emerald-400" : "text-red-400"}`} data-testid="cups-result-title">{round.status === "won" ? "You Win!" : "Try Again"}</p>
    <div className="grid grid-cols-3 gap-2 text-xs mt-3 text-gray-400">
      <div><p>Amount</p><p className="text-white font-semibold" data-testid="cups-result-amount">{fmtPKR(round.amount)}</p></div>
      <div><p>Reward ({round.reward_percent}%)</p><p className="text-white font-semibold" data-testid="cups-result-reward">{fmtPKR(round.reward)}</p></div>
      <div><p>Returned</p><p className="text-white font-semibold" data-testid="cups-result-payout">{fmtPKR(round.payout)}</p></div>
    </div>
    <Button onClick={onAgain} className="mt-4 bg-orange-600 hover:bg-orange-500 btn-glow" data-testid="cups-play-again">Play Again</Button>
  </div>
);

export default function ThreeCups() {
  const { user, setUser } = useAuth();
  const [st, setSt] = useState(null);
  const [hist, setHist] = useState([]);
  const [amount, setAmount] = useState("");
  const [round, setRound] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([api.get("/cups/state"), api.get("/cups/history")]);
    setSt(s.data); setHist(h.data); setUser({ ...user, balance: s.data.balance });
    if (s.data.open_round) { setRound(s.data.open_round); setPhase("picking"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  if (!st) return <Loader />;

  const start = async () => {
    const val = Number(amount);
    if (!val || val < st.min_bet) { toast.error(`Minimum participation is ${fmtPKR(st.min_bet)}.`); return; }
    if (val > st.max_bet) { toast.error(`Maximum participation is ${fmtPKR(st.max_bet)}.`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/cups/start", { amount: val });
      setRound(data); setPicked(null); setPhase("shuffling");
      setUser({ ...user, balance: st.balance - val }); setSt({ ...st, balance: st.balance - val });
      setTimeout(() => setPhase("picking"), 2200);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const pick = async (pos) => {
    setBusy(true); setPicked(pos);
    try {
      const { data } = await api.post(`/cups/${round.id}/pick`, { cup: pos });
      setRound(data); setPhase("revealed");
      await load();
      setPhase("revealed"); setRound(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const again = () => { setRound(null); setPicked(null); setPhase("idle"); };
  const s = st.schedule;

  return (
    <div>
      <PageHeader title="Three Cups – Find the Ball" subtitle="Pick the cup hiding the ball. Find it and get your amount back plus the reward." />
      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 ec-card ec-dark p-6 overflow-hidden" data-testid="cups-stage">
          {!s.open && phase === "idle" ? (
            <div className="h-64 flex flex-col items-center justify-center text-center" data-testid="cups-closed">
              <Ban className="w-12 h-12 text-gray-500 mb-3" />
              <p className="font-head text-3xl font-extrabold text-white">Game Closed</p>
              <p className="text-gray-400 text-sm mt-2">{CLOSED[s.closed_reason]}</p>
            </div>
          ) : (
            <>
              <p className="text-center text-sm text-gray-400" data-testid="cups-phase">
                {phase === "idle" ? "Enter an amount and press Start" : phase === "shuffling" ? "Shuffling..." : phase === "picking" ? "Pick a cup!" : "Result"}
              </p>
              <CupsBoard phase={phase} picked={picked} winning={round?.winning_cup} onPick={busy ? () => {} : pick} />
              {phase === "revealed" && round && <ResultBox round={round} onAgain={again} />}
              {round?.commit_hash && <p className="text-[10px] text-gray-600 truncate keep-ltr mt-3" data-testid="cups-commit-hash">Commitment: {round.commit_hash}</p>}
            </>
          )}
        </div>
        <div className="space-y-4">
          <div className="ec-card p-6" data-testid="cups-bet-panel">
            <div className="flex justify-between text-xs text-gray-400 mb-3">
              <span data-testid="cups-limits">Min {fmtPKR(st.min_bet)} · Max {fmtPKR(st.max_bet)}</span>
              <span data-testid="cups-balance">Balance {fmtPKR(st.balance)}</span>
            </div>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={phase !== "idle" || !s.open}
              placeholder={`Amount (${fmtPKR(st.min_bet)} – ${fmtPKR(st.max_bet)})`} className="h-12 text-lg" data-testid="cups-amount-input" />
            <p className="text-xs text-gray-500 mt-2" data-testid="cups-reward-info">Win reward: {st.reward_percent}% · e.g. Rs 1,000 returns {fmtPKR(1000 * (1 + st.reward_percent / 100))}</p>
            <Button onClick={start} disabled={busy || phase !== "idle" || !s.open} className="w-full h-12 mt-4 bg-orange-600 hover:bg-orange-500 btn-glow font-head font-bold disabled:opacity-40" data-testid="cups-start-button">START</Button>
          </div>
          <div className="ec-card p-5 text-xs text-gray-400" data-testid="cups-rules">
            <p className="text-white font-semibold mb-2">Rules & fairness</p>
            <p className="mb-2">Hours: {s.open_time === s.close_time ? "All day" : `${s.open_time} – ${s.close_time} PKT`}</p>
            <ol className="list-decimal ps-4 space-y-1">{st.rules.map((x, i) => <li key={i}>{x}</li>)}</ol>
          </div>
        </div>
      </div>
      <div className="mt-6"><CupsHistory rows={hist} /></div>
    </div>
  );
}
